import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertCircle } from 'react-feather';
import Button from '../components/UI/Button';
import { toolsConfig } from '../config/toolsConfig';
import useTranslation from '../hooks/useTranslation';

// Import tool components
import FixFlipROI from './tools/FixFlipROI';
import LeaseAnalyzer from './tools/LeaseAnalyzer';
import DealFinder from './tools/DealFinder';
import FinanceAnalyzer from './tools/FinanceAnalyzer';
import DepreciationCalculator from './tools/DepreciationCalculator';
import BuyVsRent from './tools/BuyVsRent';
import CompoundInterest from './tools/CompoundInterest';
import SalaryCalculator from './tools/SalaryCalculator';
import BRRRCalculator from './tools/BRRRCalculator';
import LoanAmortizationCalculator from './tools/FinanceLoanAmortizationCalculator';
import MortgageAmortizationCalculator from './tools/MortgageAmortizationCalculator';

// Map component names to actual components
const toolComponents = {
  FixFlipROI,
  LeaseAnalyzer,
  DealFinder,
  FinanceAnalyzer,
  DepreciationCalculator,
  BuyVsRent,
  CompoundInterest,
  SalaryCalculator,
  BRRRCalculator,
  LoanAmortizationCalculator,
  MortgageAmortizationCalculator,
  // Add more as you build them
};

const ToolPage = () => {
  const { category, toolName } = useParams();
  const categoryData = toolsConfig[category];
  const toolData = categoryData?.tools?.[toolName];
  const { t } = useTranslation();

  if (!categoryData || !toolData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen flex items-center justify-center"
      >
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('tool.notFound.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('tool.notFound.message', `The tool "${toolName}" in category "${category}" doesn't exist.`, { tool: toolName, category })}
          </p>
          <Link to="/">
            <Button>
              <ArrowLeft className="h-4 w-4" />
              {t('tool.backHome')}
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  }

  const ToolComponent = toolComponents[toolData.component];
  const IconComponent = toolData.icon;

  if (!ToolComponent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen flex items-center justify-center"
      >
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('tool.underDevelopment.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('tool.underDevelopment.message')}
          </p>
          <Link to={`/tools/${category}`}>
            <Button>
              <ArrowLeft className="h-4 w-4" />
              {t('tool.backCategory', `Back to ${categoryData?.name ?? ''}`, { category: t(`nav.${category}`, categoryData?.name ?? '') })}
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen py-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-2 text-sm">
            <Link
              to="/"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 transition-colors duration-200"
            >
              {t('tool.breadcrumb.home')}
            </Link>
            <span className="text-gray-500">/</span>
            <Link
              to={`/tools/${category}`}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 transition-colors duration-200"
            >
              {t(`nav.${category}`, categoryData.name)}
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-gray-900 dark:text-white">{t(`tool.name.${toolName}`, toolData.name)}</span>
          </div>
        </motion.nav>

        {/* Tool Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-start space-x-4 mb-6">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
              <IconComponent className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                {t(`tool.name.${toolName}`, toolData.name)}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mt-2">
                {t(`tool.desc.${toolName}`, toolData.description)}
              </p>
            </div>
          </div>

          <Link to={`/tools/${category}`}>
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4" />
              {t('tool.backCategory', `Back to ${categoryData.name}`, { category: t(`nav.${category}`, categoryData.name) })}
            </Button>
          </Link>
        </motion.div>

        {/* Tool Component */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <ToolComponent />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ToolPage;
